import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useCartContext} from "../contexts/CartContext";
import {getItem} from "../api/apiClient";
import ItemImage from "../components/ItemImage";

const CartPage = () => {
    const {cartLines, updateQuantity, removeFromCart} = useCartContext();
    const navigate = useNavigate();
    const [currentStock, setCurrentStock] = useState({});

    useEffect(() => {
        const abortController = new AbortController();
        const uniqueItemUuids = [...new Set(cartLines.map((line) => line.itemUuid))];

        Promise.all(
            uniqueItemUuids.map((itemUuid) =>
                getItem(itemUuid, abortController.signal).catch(() => null)
            )
        ).then((fetchedItems) => {
            const stockByVariantId = {};
            fetchedItems.forEach((fetchedItem) => {
                if (!fetchedItem) {
                    return;
                }
                fetchedItem.variants.forEach((variant) => {
                    stockByVariantId[variant.itemVariantId] = variant.stockQuantity;
                });
            });
            setCurrentStock(stockByVariantId);
        });

        return () => {
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const total = cartLines.reduce((sum, line) => sum + line.bruttoPrice * line.quantity, 0);
    const hasStockIssue = cartLines.some((line) => {
        const stock = currentStock[line.itemVariantId];
        return stock !== undefined && stock < line.quantity;
    });

    if (cartLines.length === 0) {
        return (
            <div className="max-w-3xl mx-auto p-4">
                <h1 className="text-2xl font-semibold text-gray-800 mb-6">Warenkorb</h1>
                <p className="text-gray-500">Dein Warenkorb ist leer.</p>
                <button
                    onClick={() => navigate("/shop")}
                    className="mt-4 text-pegasusPurple hover:underline"
                >
                    Zum Shop
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto p-4">
            <h1 className="text-2xl font-semibold text-gray-800 mb-6">Warenkorb</h1>

            <div className="space-y-4">
                {cartLines.map((line) => {
                    const stock = currentStock[line.itemVariantId];
                    const isStockIssue = stock !== undefined && stock < line.quantity;

                    return (
                        <div key={line.itemVariantId} className="flex items-center border p-4 rounded-lg">
                            <ItemImage src={line.itemImage} alt={line.itemName} className="w-16 h-16 rounded-md object-cover mr-4"/>
                            <div className="flex-1">
                                <h3 className="font-semibold text-gray-800">{line.itemName}</h3>
                                <p className="text-sm text-gray-500">
                                    {line.variantProperties.map((property) => property.value).join(" · ")}
                                </p>
                                <p className="text-sm text-gray-600">
                                    {line.bruttoPrice.toFixed(2)} {line.currency.toUpperCase()}
                                </p>
                                {isStockIssue && (
                                    <p className="text-sm font-semibold text-red-600 mt-1">
                                        {stock === 0
                                            ? "Nicht mehr verfügbar"
                                            : `Nur noch ${stock} verfügbar`}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min={1}
                                    value={line.quantity}
                                    onChange={(e) => updateQuantity(line.itemVariantId, Number(e.target.value))}
                                    className="w-16 border border-gray-300 rounded-lg px-2 py-1"
                                />
                                <button
                                    onClick={() => removeFromCart(line.itemVariantId)}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                >
                                    Entfernen
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-between items-center mt-6 border-t pt-4">
                <p className="font-bold text-gray-800">
                    Gesamt: {total.toFixed(2)} {cartLines[0]?.currency.toUpperCase()}
                </p>
                <button
                    onClick={() => navigate("/cart/checkout")}
                    disabled={hasStockIssue}
                    className={`py-2 px-6 rounded-lg font-semibold ${
                        hasStockIssue
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                    }`}
                >
                    Zur Kasse
                </button>
            </div>
            {hasStockIssue && (
                <p className="text-sm text-red-600 mt-2 text-right">
                    Bitte passe die markierten Mengen an, bevor du fortfährst.
                </p>
            )}
        </div>
    );
};

export default CartPage;
